/**
 * PlanovaniPage — Phase 5.1 servis weekly schedule.
 *
 * Layout: Shell · AppRail (servis active) · Crown (breadcrumb · Týden N · meta
 * · subtabs) · band (prev/next + week label + view-mode segmented + chips) ·
 * Calendar.SwimGrid swimlane · Inspector with selected-day stats.
 *
 * Source: .agents/design/project/artboard-radical-plan.jsx
 *
 * Week × technician × 30-min-slot grid. Write actions (events, vacations,
 * internal events, shifts) go through ServisEventModal.
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Fragment, lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AgendaView } from '@/components/AgendaView';
import { AppRail } from '@/components/AppRail';
import { useChannel } from '@/lib/pubnub';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Calendar } from '@/components/Calendar';
import { Chip } from '@/components/Chip';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/Button';
import { Marker } from '@/components/Marker';
import { EventChip } from '@/components/EventChip';
import { Icon } from '@/components/Icon';
import { Inspector } from '@/components/Inspector';
import { Pill } from '@/components/Pill';
import { SegmentedControl } from '@/components/SegmentedControl';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { Menu } from '@/components/Menu';
import { formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useServisTabs, type ServisTab } from './tabs';
import {
  useServisWeekQuery,
  type ServisDay,
  type ServisEvent,
  type ServisMechanic,
} from './api';
import type { ServisEventType } from './ServisEventModal';

// Modal pulls react-hook-form + zod (~30 KB gzip). Defer the chunk fetch to
// the first time the user opens the modal — after that we keep it mounted so
// Radix's close animation runs.
const ServisEventModal = lazy(() =>
  import('./ServisEventModal').then((m) => ({ default: m.ServisEventModal })),
);
import { ServisPotvrzeniModal } from './ServisPotvrzeni';
import {
  SLOTS_PER_DAY,
  SLOT_LABELS,
  agendaHourGroups,
  authorColor,
  eventSlotRange,
  eventChipTone,
  eventSubtitle,
  eventTimeHM,
  eventTitle,
  eventTone,
  formatDayDate,
  formatWeekRange,
  isEventNow,
  isSlotInPast,
  mechanicFirstLast,
  POTVRZENI_LABEL,
  POTVRZENI_MARKER_TONE,
  potvrzeniStatus,
  slotToLocalInput,
  ukonText,
} from './helpers';

type ViewMode = 'den' | 'tyden';

const VIEW_MODE_OPTIONS = [
  { value: 'den' as ViewMode, label: 'Den' },
  { value: 'tyden' as ViewMode, label: 'Týden' },
];

const VIEW_MODE_STORAGE_KEY = 'servis-planovani-view-mode';

function readStoredViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'tyden';
  const raw = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return raw === 'den' || raw === 'tyden' ? raw : 'tyden';
}

const TECH_FILTER_STORAGE_KEY = 'servis-planovani-tech-filter';

function readStoredTechFilter(): Set<number> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(TECH_FILTER_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((n): n is number => typeof n === 'number'));
    }
  } catch {
    // Corrupt / unavailable storage — fall back to "show all".
  }
  return new Set();
}

export function PlanovaniPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Phone swaps the swimlane for the agenda (the 20-slot grid needs ≥1,080px);
  // tablet + desktop keep the swimlane. Component swap, not CSS hiding.
  const { isPhone } = useBreakpoint();
  const urlSearch = useSearch({ from: '/servis/planovani' });
  const offset = urlSearch.offset ?? 0;

  // Stage 8: uskladněni-only members (servis:none) land on /servis/planovani
  // via the rail's Servis item — the planner would just 403 on them, so send
  // them to the tyre-storage list they actually may read instead.
  const { can, resolved } = useModuleAccess();
  const tabOptions = useServisTabs();
  const uskladneniOnly =
    resolved && !can('servis', 'read') && can('uskladneni', 'read');
  useEffect(() => {
    if (uskladneniOnly) {
      navigate({ to: '/servis/uskladneni', replace: true });
    }
  }, [uskladneniOnly, navigate]);

  // PubNub realtime — Phase 5.6. When another client mutates a servis event
  // the API publishes to the `servis` channel; refresh the week + search +
  // event-detail caches so this tab reflects the change without a reload.
  useChannel(
    'servis',
    useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['servis'] });
    }, [queryClient]),
  );
  const setOffset = (next: number) => {
    navigate({
      to: '/servis/planovani',
      search: { offset: next === 0 ? undefined : next },
      replace: true,
    });
  };

  const weekQuery = useServisWeekQuery(offset);
  const data = weekQuery.data;

  const days = data?.days ?? [];
  const mechanics = data?.mechanics ?? [];
  const allEvents = useMemo(
    () => days.flatMap((d) => d.events),
    [days],
  );

  // Technician filter — empty set means "show all". Stays a visual filter only;
  // the crown KPIs keep reflecting the full mechanic roster so utilization
  // numbers stay stable when you toggle a single tech.
  const [selectedTechs, setSelectedTechs] = useState<Set<number>>(readStoredTechFilter);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        TECH_FILTER_STORAGE_KEY,
        JSON.stringify([...selectedTechs]),
      );
    } catch {
      // localStorage unavailable — in-memory filter still drives this session.
    }
  }, [selectedTechs]);
  const visibleMechanics = useMemo(() => {
    if (selectedTechs.size === 0) return mechanics;
    return mechanics.filter((m) => selectedTechs.has(m.id_opravce));
  }, [mechanics, selectedTechs]);
  const techFilterLabel =
    selectedTechs.size === 0
      ? 'Všichni'
      : selectedTechs.size === 1
        ? mechanicFirstLast(
            mechanics.find((m) =>
              selectedTechs.has(m.id_opravce),
            )?.jmeno ?? '',
          ).first || '1 technik'
        : `${selectedTechs.size} techniků`;

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [viewMode, setViewModeState] = useState<ViewMode>(readStoredViewMode);
  const setViewMode = useCallback((next: ViewMode) => {
    setViewModeState(next);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
    } catch {
      // localStorage unavailable (private mode, quota) — in-memory state
      // still drives the current session.
    }
  }, []);
  // Modal state for Phase 5.7 — either editing an existing event (editId set)
  // or creating a fresh one (createType + preset hints).
  const [modal, setModal] = useState<{
    editId?: number;
    createType?: ServisEventType;
    presetDatumOd?: string;
    presetMechanicId?: number;
  } | null>(null);
  const [modalEverOpened, setModalEverOpened] = useState(false);
  useEffect(() => {
    if (modal !== null) setModalEverOpened(true);
  }, [modal]);
  // Clicking an empty slot opens the same create-type menu as the toolband
  // "Naplánovat" button, anchored at the cursor, with the day + technician
  // pre-filled. Mirrors the legacy slot-click behaviour.
  const [slotMenu, setSlotMenu] = useState<{
    x: number;
    y: number;
    presetDatumOd: string;
    presetMechanicId: number;
  } | null>(null);
  const openCreate = (createType: ServisEventType, opts: { presetDatumOd?: string; presetMechanicId?: number } = {}) => {
    setModal({ createType, ...opts });
  };
  const openEdit = (editId: number) => {
    setModal({ editId });
  };
  const closeModal = () => setModal(null);
  // "Did it happen?" dialog, opened from the inspector's "K potvrzení" list.
  const [potvrzeniId, setPotvrzeniId] = useState<number | null>(null);
  const createMenuItems = (opts: {
    presetDatumOd?: string;
    presetMechanicId?: number;
  }) => (
    <>
      <Menu.Label>Vytvořit</Menu.Label>
      <Menu.Item onSelect={() => openCreate(0, opts)}>Servisní úkon</Menu.Item>
      <Menu.Item onSelect={() => openCreate(2, opts)}>Interní úkon</Menu.Item>
      <Menu.Item onSelect={() => openCreate(1, opts)}>Dovolená</Menu.Item>
      <Menu.Item onSelect={() => openCreate(3, opts)}>Směny pro týden</Menu.Item>
    </>
  );
  // After a week-shift triggered from day-mode arrows, snap the selected day
  // to the first / last day of the freshly-loaded week. The intent is consumed
  // when the new week's `days` array shows up.
  const [pendingDayPosition, setPendingDayPosition] = useState<
    'first' | 'last' | null
  >(null);

  useEffect(() => {
    if (!pendingDayPosition || days.length === 0) return;
    const target =
      pendingDayPosition === 'first'
        ? days[0].date
        : days[days.length - 1].date;
    setSelectedDate(target);
    setPendingDayPosition(null);
  }, [pendingDayPosition, days]);

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const effectiveSelectedDate = useMemo(() => {
    // Clamp to whatever week is currently loaded. After a day-mode rollover
    // the stored `selectedDate` belongs to the previous week — fall back via
    // the pending-position intent so the day-view stays a single column
    // rather than flashing the whole week.
    if (selectedDate && days.some((d) => d.date === selectedDate)) {
      return selectedDate;
    }
    if (pendingDayPosition === 'last' && days.length > 0) {
      return days[days.length - 1].date;
    }
    if (pendingDayPosition === 'first' && days.length > 0) {
      return days[0].date;
    }
    const todayDay = days.find((d) => d.date === todayKey);
    return todayDay?.date ?? days[0]?.date ?? null;
  }, [selectedDate, days, todayKey, pendingDayPosition]);

  const selectedDay = days.find((d) => d.date === effectiveSelectedDate) ?? null;
  const visibleDays = viewMode === 'den' && selectedDay ? [selectedDay] : days;
  // Inspector summary scope. Day-view always summarizes the selected day.
  // Week-view summarizes the whole week ONLY when exactly one technician is
  // filtered (to see that person's week); with no filter or several it stays
  // the selected-day summary.
  const inspectorScope: ViewMode =
    viewMode === 'tyden' && selectedTechs.size === 1 ? 'tyden' : 'den';
  const inspectorDays =
    inspectorScope === 'tyden' ? days : selectedDay ? [selectedDay] : [];

  // Phone hides the Den/Týden toggle — the day strip selects days, so the
  // band arrows always step whole weeks there regardless of the stored mode.
  const arrowsMode: ViewMode = isPhone ? 'tyden' : viewMode;

  const handlePrev = () => {
    if (arrowsMode === 'tyden') {
      setOffset(offset - 1);
      return;
    }
    const idx = days.findIndex((d) => d.date === effectiveSelectedDate);
    if (idx > 0) {
      setSelectedDate(days[idx - 1].date);
    } else {
      setOffset(offset - 1);
      setPendingDayPosition('last');
    }
  };

  const handleNext = () => {
    if (arrowsMode === 'tyden') {
      setOffset(offset + 1);
      return;
    }
    const idx = days.findIndex((d) => d.date === effectiveSelectedDate);
    if (idx >= 0 && idx < days.length - 1) {
      setSelectedDate(days[idx + 1].date);
    } else {
      setOffset(offset + 1);
      setPendingDayPosition('first');
    }
  };

  const arrowLabels =
    arrowsMode === 'den'
      ? { prev: 'Předchozí den', next: 'Následující den' }
      : { prev: 'Předchozí týden', next: 'Následující týden' };

  const totals = useMemo(() => {
    const techIds = new Set(allEvents.map((e) => e.id_opravce));
    const totalSpan = allEvents.reduce((sum, e) => {
      const day = days.find((d) => d.date === e.datum_od.slice(0, 10));
      if (!day) return sum;
      const range = eventSlotRange(e, day);
      return sum + (range?.span ?? 0);
    }, 0);
    const capacity = mechanics.length * SLOTS_PER_DAY * Math.max(1, days.length);
    const utilization = capacity > 0 ? Math.round((totalSpan / capacity) * 100) : 0;
    return {
      events: allEvents.length,
      techsBusy: techIds.size,
      totalTechs: mechanics.length,
      utilization,
      vacations: allEvents.filter((e) => e.typ_udalosti === 1).length,
    };
  }, [allEvents, mechanics, days]);

  // Phone agenda: the selected day's events in hour groups + the utilization
  // card numbers, both scoped to the visible (filtered) technicians.
  const agendaGroups = useMemo(
    () => (selectedDay ? agendaHourGroups(selectedDay, selectedTechs) : []),
    [selectedDay, selectedTechs],
  );
  const agendaStats = useMemo(() => {
    if (!selectedDay) return null;
    const ids = new Set(visibleMechanics.map((m) => m.id_opravce));
    const evs = selectedDay.events.filter((e) => ids.has(e.id_opravce));
    const span = evs.reduce(
      (sum, e) => sum + (eventSlotRange(e, selectedDay)?.span ?? 0),
      0,
    );
    const capacity = Math.max(1, visibleMechanics.length * SLOTS_PER_DAY);
    return {
      utilization: Math.round((span / capacity) * 100),
      servis: evs.filter((e) => e.typ_udalosti === 0).length,
      interni: evs.filter((e) => e.typ_udalosti === 2).length,
      dovolena: evs.filter((e) => e.typ_udalosti === 1).length,
      smeny: evs.filter((e) => e.typ_udalosti === 3).length,
    };
  }, [selectedDay, visibleMechanics]);

  // Redirecting (see the effect above) — don't flash the 403-ing planner.
  if (uskladneniOnly) return null;

  return (
    <Shell
      rail={<AppRail active="servis" />}
      inspector={
        <ScheduleInspector
          scope={inspectorScope}
          days={inspectorDays}
          mechanics={visibleMechanics}
          filtered={selectedTechs.size > 0}
          weekLabel={
            days.length > 0
              ? formatWeekRange(days[0].date, days[days.length - 1].date)
              : ''
          }
          isLoading={weekQuery.isLoading}
          serverNow={data?.serverNow}
          onConfirm={setPotvrzeniId}
        />
      }
    >
      <Crown>
        <Breadcrumb
          segments={[
            { label: <Link to="/servis/planovani">Servis</Link> },
            { label: 'Plánování' },
          ]}
        />
        <Crown.Row className="mt-3">
          <DisplayHeading>
            Týden {data?.week.number ?? '—'}, <em>{data?.week.year ?? ''}</em>
          </DisplayHeading>
          <CrownMeta
            phoneCols={4}
            items={[
              { label: 'Úkonů', value: formatInt(totals.events) },
              {
                label: 'Techniků',
                value: `${formatInt(totals.techsBusy)}/${formatInt(totals.totalTechs)}`,
              },
              { label: 'Vytížení', value: `${formatInt(totals.utilization)} %` },
              { label: 'Dovolená', value: formatInt(totals.vacations) },
            ]}
          />
        </Crown.Row>
        <Crown.Row className="mt-3">
          <Subtabs<ServisTab>
            aria-label="Servis sekce"
            items={tabOptions.map((o) => ({
              value: o.value,
              label: o.label,
              ...(o.value === 'planovani' ? { count: totals.events } : {}),
            }))}
            value="planovani"
            onChange={(next) => {
              if (next === 'vyhledani') {
                navigate({ to: '/servis/vyhledani' });
              } else if (next === 'uskladneni') {
                navigate({ to: '/servis/uskladneni' });
              } else if (next === 'historie') {
                navigate({ to: '/servis/historie' });
              } else if (next === 'lokace') {
                navigate({ to: '/servis/lokace' });
              } else if (next === 'sprava') {
                navigate({ to: '/servis/sprava' });
              }
            }}
          />
        </Crown.Row>
      </Crown>

      <div
        data-testid="planovani-band"
        className="flex items-center gap-3.5 border-b border-line bg-paper px-8 py-3.5 max-lg:flex-wrap max-lg:gap-x-3 max-lg:gap-y-2 max-sm:px-4 max-sm:py-2.5"
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={arrowLabels.prev}
            onClick={handlePrev}
            className="grid size-7 place-items-center border border-line bg-paper text-ink-2 hover:border-ink hover:text-ink max-sm:size-10"
          >
            <Icon name="chevronL" size={13} />
          </button>
          <button
            type="button"
            aria-label={arrowLabels.next}
            onClick={handleNext}
            className="grid size-7 place-items-center border border-line bg-paper text-ink-2 hover:border-ink hover:text-ink max-sm:size-10"
          >
            <Icon name="chevronR" size={13} />
          </button>
        </div>
        <div className="flex items-baseline gap-2.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
            Týden
          </span>
          <span className="text-[26px] font-bold leading-none tracking-[-0.02em] text-ink max-sm:text-[20px]">
            {data?.week.number ?? '—'}
          </span>
          <span className="text-[13px] text-ink-2">
            {data && days.length > 0
              ? formatWeekRange(days[0].date, days[days.length - 1].date)
              : ''}
          </span>
        </div>
        {offset !== 0 && (
          <button
            type="button"
            onClick={() => setOffset(0)}
            className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-accent-text hover:underline"
          >
            Zpět na dnešní týden
          </button>
        )}
        <div className="flex-1" />
        <SegmentedControl<ViewMode>
          aria-label="Pohled"
          options={VIEW_MODE_OPTIONS}
          value={viewMode}
          onChange={setViewMode}
          className="max-sm:hidden"
        />
        <Menu>
          <Menu.Trigger asChild>
            <Chip
              icon={<Icon name="users" size={12} />}
              active={selectedTechs.size > 0}
            >
              {techFilterLabel}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Filtr techniků</Menu.Label>
            <Menu.Item onSelect={() => setSelectedTechs(new Set())}>
              Všichni technici
            </Menu.Item>
            <Menu.Separator />
            {mechanics.map((m) => (
              <Menu.CheckItem
                key={m.id_opravce}
                checked={selectedTechs.has(m.id_opravce)}
                onCheckedChange={(checked) => {
                  setSelectedTechs((prev) => {
                    const next = new Set(prev);
                    if (checked) next.add(m.id_opravce);
                    else next.delete(m.id_opravce);
                    return next;
                  });
                }}
              >
                {m.jmeno}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <Menu>
          <Menu.Trigger>
            <Chip icon={<Icon name="plus" size={12} />} disabled={data?.readOnly}>
              Naplánovat
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            {createMenuItems({ presetDatumOd: effectiveSelectedDate ?? undefined })}
          </Menu.Content>
        </Menu>
        {data?.readOnly && (
          <Pill className="ml-1">Jen pro čtení</Pill>
        )}
      </div>

      {isPhone && days.length > 0 && (
        <AgendaView.DayStrip
          days={days.map((d) => ({
            key: d.date,
            dow: d.dayName.slice(0, 2),
            dayNum: parseInt(d.date.slice(8, 10), 10),
            active: d.date === effectiveSelectedDate,
            muted: isWeekend(d.date),
            pip: d.events.length > 0,
          }))}
          onSelect={setSelectedDate}
        />
      )}
      <div
        data-testid="planovani-page"
        className={
          isPhone
            ? 'min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-3.5'
            : 'flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3.5'
        }
      >
        {weekQuery.isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : weekQuery.isError ? (
          <EmptyState
            eyebrow="Chyba"
            title="Plánování se nepodařilo načíst"
            description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
          />
        ) : mechanics.length === 0 ? (
          <EmptyState
            eyebrow="Prázdné"
            title="Žádní technici"
            description="Přidejte techniky v sekci Servis a vraťte se sem."
          />
        ) : isPhone ? (
          <>
            {selectedDay && agendaStats && (
              <AgendaView.UtilCard
                title={
                  <>
                    {selectedDay.dayName}{' '}
                    <em>{formatDayDate(selectedDay.date)}</em>
                  </>
                }
                pct={agendaStats.utilization}
                stats={[
                  { label: 'Servis', value: formatInt(agendaStats.servis) },
                  { label: 'Interní', value: formatInt(agendaStats.interni) },
                  { label: 'Dovolená', value: formatInt(agendaStats.dovolena) },
                  { label: 'Směny', value: formatInt(agendaStats.smeny) },
                ]}
              />
            )}
            <AgendaView>
              {agendaGroups.length === 0 ? (
                <p className="pt-4 text-[12px] text-ink-3">
                  Žádné naplánované úkony.
                </p>
              ) : (
                agendaGroups.map((group) => (
                  <Fragment key={group.hour}>
                    <AgendaView.Hour>{group.hour}</AgendaView.Hour>
                    {group.items.map(({ event, time }) => {
                      const tech = mechanics.find(
                        (m) => m.id_opravce === event.id_opravce,
                      );
                      return (
                        <AgendaView.Job
                          key={event.id_udalosti}
                          time={time}
                          title={eventTitle(event)}
                          now={isEventNow(event, data?.serverNow)}
                          task={agendaTask(event)}
                          tone={eventChipTone(event)}
                          status={potvrzeniStatus(event)}
                          meta={
                            tech ? (
                              <span className="flex items-center gap-1.5 text-ink-2">
                                <span
                                  aria-hidden
                                  className="size-1.5 rounded-full bg-ok"
                                />
                                {tech.jmeno}
                              </span>
                            ) : undefined
                          }
                          metaRight={(event.spz_vozidla || event.vin || '').trim() || undefined}
                          onClick={() => openEdit(event.id_udalosti)}
                        />
                      );
                    })}
                  </Fragment>
                ))
              )}
            </AgendaView>
          </>
        ) : (
          <Calendar scroll scrollResetKey={`${viewMode}:${visibleDays[0]?.date ?? ''}`}>
            <Calendar.SwimGrid
              resourceCols={['72px', '128px']}
              slots={SLOTS_PER_DAY}
              minWidth={1240}
            >
              <Calendar.SwimCorner>Datum / Technik</Calendar.SwimCorner>
              <Calendar.SwimSlotHeader slots={SLOT_LABELS} />

              {visibleDays.map((day, dIdx) => {
                const isToday = day.date === todayKey;
                const isSelected = day.date === effectiveSelectedDate;
                return (
                  <Calendar.SwimDay
                    key={day.date}
                    rows={visibleMechanics.length}
                    dayLabel={
                      <button
                        type="button"
                        onClick={() => setSelectedDate(day.date)}
                        className={
                          'cursor-pointer text-left ' +
                          (isSelected ? 'underline decoration-accent decoration-2 underline-offset-4' : '')
                        }
                      >
                        {day.dayName}
                      </button>
                    }
                    dayDate={formatDayDate(day.date)}
                    today={isToday}
                    todayMarker="— Dnes"
                    leadingDivider={dIdx > 0}
                  >
                    {visibleMechanics.map((mech) => {
                      const { first, last } = mechanicFirstLast(mech.jmeno);
                      const evs = day.events.filter(
                        (e) => e.id_opravce === mech.id_opravce,
                      );
                      return (
                        <Calendar.SwimRow
                          key={day.date + ':' + mech.id_opravce}
                          slots={SLOTS_PER_DAY}
                          rowPx={viewMode === 'den' ? 86 : 64}
                          onSlotClick={
                            data?.readOnly
                              ? undefined
                              : (slot, ev) =>
                                  setSlotMenu({
                                    x: ev.clientX,
                                    y: ev.clientY,
                                    presetDatumOd: slotToLocalInput(day.date, slot),
                                    presetMechanicId: mech.id_opravce,
                                  })
                          }
                          slotDisabled={(slot) =>
                            isSlotInPast(day.date, slot, data?.serverNow)
                          }
                          slotLabel={(slot) =>
                            `Naplánovat — ${mech.jmeno}, ${day.dayName} ${SLOT_LABELS[slot]}`
                          }
                          resource={
                            <>
                              <span className="text-[11.5px] font-semibold leading-tight tracking-[-0.01em] text-ink">
                                {first}
                              </span>
                              <span className="text-[10.5px] text-ink-3">{last}</span>
                            </>
                          }
                        >
                          {evs.map((e) => {
                            const range = eventSlotRange(e, day);
                            if (!range) return null;
                            return (
                              <EventChip
                                key={e.id_udalosti}
                                tone={eventChipTone(e)}
                                status={potvrzeniStatus(e)}
                                title={eventTitle(e)}
                                sub={eventSubtitle(e)}
                                autoSizeSub={viewMode === 'den'}
                                accentDot={authorColor(e.barva)}
                                start={range.start}
                                span={range.span}
                                totalSlots={SLOTS_PER_DAY}
                                onClick={() => openEdit(e.id_udalosti)}
                              />
                            );
                          })}
                        </Calendar.SwimRow>
                      );
                    })}
                  </Calendar.SwimDay>
                );
              })}
            </Calendar.SwimGrid>
          </Calendar>
        )}
      </div>
      <Menu
        open={slotMenu !== null}
        onOpenChange={(o) => {
          if (!o) setSlotMenu(null);
        }}
      >
        <Menu.Trigger asChild>
          <span
            aria-hidden
            style={{
              position: 'fixed',
              left: slotMenu?.x ?? 0,
              top: slotMenu?.y ?? 0,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          />
        </Menu.Trigger>
        <Menu.Content side="bottom" align="start">
          {createMenuItems({
            presetDatumOd: slotMenu?.presetDatumOd,
            presetMechanicId: slotMenu?.presetMechanicId,
          })}
        </Menu.Content>
      </Menu>
      <ServisPotvrzeniModal
        eventId={potvrzeniId}
        onOpenChange={(o) => {
          if (!o) setPotvrzeniId(null);
        }}
        onReschedule={openEdit}
      />
      {modalEverOpened ? (
        <Suspense fallback={null}>
          <ServisEventModal
            open={modal !== null}
            onOpenChange={(o) => {
              if (!o) closeModal();
            }}
            editId={modal?.editId ?? null}
            createType={modal?.createType}
            presetDatumOd={modal?.presetDatumOd}
            presetMechanicId={modal?.presetMechanicId}
          />
        </Suspense>
      ) : null}
    </Shell>
  );
}

interface ScheduleInspectorProps {
  scope: ViewMode;
  /** Days in scope: a single day in day-view, the whole week in week-view. */
  days: ServisDay[];
  /** People to summarize — the filtered techs (all of them when no filter). */
  mechanics: ServisMechanic[];
  /** Whether a technician filter is active (drives the people line + title). */
  filtered: boolean;
  /** Week range label, used as the title in week scope. */
  weekLabel: string;
  isLoading: boolean;
  serverNow?: string;
  /** Open the "did it happen?" dialog for an event. */
  onConfirm: (id: number) => void;
}

function shortDate(iso: string): string {
  const [, mm, dd] = iso.split('-');
  return `${Number(dd)}. ${Number(mm)}.`;
}

function isWeekend(iso: string): boolean {
  const dow = new Date(iso + 'T00:00:00').getDay();
  return dow === 0 || dow === 6;
}

/**
 * Agenda job second line — vozidlo + úkon, minus whatever the title already
 * shows. Unlike `eventSubtitle` it leaves the SPZ out: that rides the job's
 * meta row (right-aligned), per the handoff's PhoneServis card.
 */
function agendaTask(event: ServisEvent): string | undefined {
  const title = eventTitle(event);
  const used = (s: string | null | undefined) => (s ?? '').trim() === title;
  const parts = [
    used(event.vozidlo) ? '' : (event.vozidlo ?? '').trim(),
    used(ukonText(event)) ? '' : ukonText(event),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function ScheduleInspector({
  scope,
  days,
  mechanics,
  filtered,
  weekLabel,
  isLoading,
  serverNow,
  onConfirm,
}: ScheduleInspectorProps) {
  const eyebrow = <>— {scope === 'den' ? 'Vybraný den' : 'Vybraný týden'}</>;

  if (isLoading) {
    return (
      <Inspector>
        <Inspector.Head eyebrow={eyebrow} title="Načítám…" />
        <Inspector.Body>
          <Skeleton className="h-32 w-full" />
        </Inspector.Body>
      </Inspector>
    );
  }
  if (days.length === 0) {
    return (
      <Inspector>
        <Inspector.Head eyebrow={eyebrow} title="Žádný den" />
        <Inspector.Body>
          <p className="text-[12px] text-ink-3">
            Vyberte den v rozvrhu pro zobrazení statistik.
          </p>
        </Inspector.Body>
      </Inspector>
    );
  }
  if (mechanics.length === 0) {
    return (
      <Inspector>
        <Inspector.Head eyebrow={eyebrow} title="Žádný technik" />
        <Inspector.Body>
          <p className="text-[12px] text-ink-3">
            Vybraní technici nejsou v tomto rozvrhu.
          </p>
        </Inspector.Body>
      </Inspector>
    );
  }

  const mechIds = new Set(mechanics.map((m) => m.id_opravce));
  const pairs = days
    .flatMap((d) => d.events.map((e) => ({ e, d })))
    .filter(({ e }) => mechIds.has(e.id_opravce))
    .sort((a, b) => a.e.datum_od.localeCompare(b.e.datum_od));

  const techsBusy = new Set(pairs.map(({ e }) => e.id_opravce)).size;
  const totalSpan = pairs.reduce(
    (sum, { e, d }) => sum + (eventSlotRange(e, d)?.span ?? 0),
    0,
  );
  const capacity = Math.max(1, mechanics.length * SLOTS_PER_DAY * days.length);
  const utilization = Math.round((totalSpan / capacity) * 100);

  // Week scope splits utilization into working days (Mon–Fri) vs weekend,
  // since a flat 7-day average understates weekday load.
  const utilFor = (groupDays: ServisDay[]): number | null => {
    if (groupDays.length === 0) return null;
    const dateSet = new Set(groupDays.map((d) => d.date));
    const span = pairs
      .filter(({ d }) => dateSet.has(d.date))
      .reduce((s, { e, d }) => s + (eventSlotRange(e, d)?.span ?? 0), 0);
    const cap = Math.max(1, mechanics.length * SLOTS_PER_DAY * groupDays.length);
    return Math.round((span / cap) * 100);
  };
  const workingUtil = utilFor(days.filter((d) => !isWeekend(d.date)));
  const weekendUtil = utilFor(days.filter((d) => isWeekend(d.date)));

  const stats = {
    servis: pairs.filter(({ e }) => e.typ_udalosti === 0).length,
    interni: pairs.filter(({ e }) => e.typ_udalosti === 2).length,
    dovolena: pairs.filter(({ e }) => e.typ_udalosti === 1).length,
    smena: pairs.filter(({ e }) => e.typ_udalosti === 3).length,
  };

  const title =
    scope === 'den' ? (
      <span>
        {days[0].dayName},
        <br />
        <em className="font-medium italic text-ink-2">
          {formatDayDate(days[0].date)}.
        </em>
      </span>
    ) : (
      <span>
        Týden,
        <br />
        <em className="font-medium italic text-ink-2">{weekLabel}</em>
      </span>
    );

  const taskPairs = pairs.filter(({ e }) => e.typ_udalosti !== 3);
  const pendingPairs = pairs.filter(({ e }) => e.k_potvrzeni);

  return (
    <Inspector>
      <Inspector.Head eyebrow={eyebrow} title={title} />
      <Inspector.Body className="flex flex-col gap-6">
        {filtered && (
          <section>
            <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
              {mechanics.length === 1 ? 'Technik' : 'Technici'}
            </div>
            <p className="text-[12px] text-ink">
              {mechanics.map((m) => m.jmeno).join(', ')}
            </p>
          </section>
        )}

        {scope === 'den' ? (
          <section>
            <UtilBar label="Vytížení dne" utilization={utilization} />
            <div className="mt-1.5 flex justify-end text-[11px] text-ink-3">
              <span className="font-mono">
                {techsBusy}/{mechanics.length} techniků
              </span>
            </div>
          </section>
        ) : (
          <section className="flex flex-col gap-4">
            {workingUtil !== null && (
              <UtilBar label="Vytížení — pracovní dny" utilization={workingUtil} />
            )}
            {weekendUtil !== null && (
              <UtilBar label="Vytížení — víkend" utilization={weekendUtil} />
            )}
            <div className="flex justify-end text-[11px] text-ink-3">
              <span className="font-mono">
                {techsBusy}/{mechanics.length} techniků
              </span>
            </div>
          </section>
        )}

        <section className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Stat label="Servis" value={stats.servis} />
          <Stat label="Interní" value={stats.interni} />
          <Stat label="Dovolená" value={stats.dovolena} />
          <Stat label="Směny" value={stats.smena} />
        </section>

        {pendingPairs.length > 0 && (
          <section data-testid="k-potvrzeni">
            <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-warn">
              — K potvrzení · {pendingPairs.length}
            </div>
            <ul className="flex flex-col">
              {pendingPairs.map(({ e, d }) => (
                <PendingRow
                  key={e.id_udalosti}
                  event={e}
                  mechanics={mechanics}
                  dateLabel={scope === 'tyden' ? shortDate(d.date) : undefined}
                  onConfirm={onConfirm}
                />
              ))}
            </ul>
          </section>
        )}

        <section>
          <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
            — Další úkony
          </div>
          {taskPairs.length === 0 ? (
            <p className="text-[12px] text-ink-3">Žádné naplánované úkony.</p>
          ) : (
            <ul className="flex flex-col">
              {taskPairs.map(({ e, d }) => (
                <UpcomingRow
                  key={e.id_udalosti}
                  event={e}
                  mechanics={mechanics}
                  isNow={isEventNow(e, serverNow)}
                  dateLabel={scope === 'tyden' ? shortDate(d.date) : undefined}
                />
              ))}
            </ul>
          )}
        </section>
      </Inspector.Body>
    </Inspector>
  );
}

function UtilBar({ label, utilization }: { label: string; utilization: number }) {
  return (
    <div>
      <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
        {label}
      </div>
      <div
        className="relative h-1.5 overflow-hidden bg-bg"
        role="progressbar"
        aria-label={`Vytížení — ${label}`}
        aria-valuenow={utilization}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="absolute inset-y-0 left-0 bg-accent"
          style={{ width: `${Math.min(100, utilization)}%` }}
        />
      </div>
      <div className="mt-1.5 text-[11px] text-ink-3">{utilization} % kapacity</div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
        {label}
      </div>
      <div className="text-[24px] font-bold leading-none tracking-[-0.02em] text-ink">
        {formatInt(value)}
      </div>
    </div>
  );
}

const TONE_DOT: Record<string, string> = {
  ok: 'bg-ok',
  accent: 'bg-accent',
  warn: 'bg-warn',
  neutral: 'bg-ink-3',
  ink: 'bg-ink',
};

// Soft fill matching the event's calendar chip (EventChip TONE_CLASSES), used
// to wash the row when the event is happening right now.
const TONE_NOW_BG: Record<string, string> = {
  ok: 'bg-ok-soft',
  accent: 'bg-accent-soft',
  warn: 'bg-warn-soft',
  neutral: 'bg-surface-2',
  ink: 'bg-surface-2',
};

/** An event that started, is still unconfirmed, and can be confirmed with one click. */
function PendingRow({
  event,
  mechanics,
  dateLabel,
  onConfirm,
}: {
  event: ServisEvent;
  mechanics: ServisMechanic[];
  dateLabel?: string;
  onConfirm: (id: number) => void;
}) {
  const tech = mechanics.find((m) => m.id_opravce === event.id_opravce);
  return (
    <li className="flex items-center gap-3 border-t border-line py-2.5 last:border-b last:border-b-line">
      <span className="w-14 shrink-0 font-mono text-[11px] tracking-[-0.02em] text-ink-2">
        {dateLabel && <span className="block text-[9.5px] text-ink-3">{dateLabel}</span>}
        {eventTimeHM(event.datum_od)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-semibold tracking-[-0.01em] text-ink">
          {eventTitle(event)}
        </span>
        {tech && <span className="block truncate text-[11px] text-ink-3">{tech.jmeno}</span>}
      </span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => onConfirm(event.id_udalosti)}
        data-testid={`potvrdit-${event.id_udalosti}`}
      >
        Potvrdit
      </Button>
    </li>
  );
}

function UpcomingRow({
  event,
  mechanics,
  isNow = false,
  dateLabel,
}: {
  event: ServisEvent;
  mechanics: ServisMechanic[];
  isNow?: boolean;
  dateLabel?: string;
}) {
  const tone = eventTone(event);
  const tech = mechanics.find((m) => m.id_opravce === event.id_opravce);
  const sub = [tech?.jmeno, eventSubtitle(event)].filter(Boolean).join(' · ');
  const status = potvrzeniStatus(event);
  return (
    <li
      className={
        'flex items-start gap-3 border-t border-line py-2.5 last:border-b last:border-b-line ' +
        (isNow ? (TONE_NOW_BG[tone] ?? 'bg-surface-2') + ' -mx-3 px-3' : '')
      }
    >
      <span className="w-14 shrink-0 pt-px font-mono text-[11px] tracking-[-0.02em] text-ink-2">
        {dateLabel && (
          <span className="block text-[9.5px] text-ink-3">{dateLabel}</span>
        )}
        {eventTimeHM(event.datum_od)}
      </span>
      <span
        className={'mt-1.5 block size-2 shrink-0 rounded-full ' + (TONE_DOT[tone] ?? 'bg-ink-3')}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-1.5">
          <span className="block text-[12px] font-semibold tracking-[-0.01em] text-ink">
            {eventTitle(event)}
          </span>
          {isNow && (
            <span className="mt-0.5 shrink-0 font-mono text-[9px] uppercase tracking-[0.08em] text-accent-text">
              Nyní
            </span>
          )}
          {status && (
            <Marker
              tone={POTVRZENI_MARKER_TONE[status]}
              className="ml-auto shrink-0 !text-[9.5px]"
            >
              {POTVRZENI_LABEL[status]}
            </Marker>
          )}
        </span>
        {sub && <span className="text-[11px] text-ink-3">{sub}</span>}
      </span>
    </li>
  );
}
